import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import Pagination from '../../components/common/Pagination';
import MultiSelectDropdown from '../../components/common/MultiSelectDropdown';
import SortableTh, { nextSort } from '../../components/common/SortableTh';
import { FiSearch, FiPlus, FiX } from 'react-icons/fi';
import { MdAttachFile } from 'react-icons/md';
import { FaCrown } from 'react-icons/fa6';
import { getDefaultItemsPerPage, getDefaultItemsPerPageSync } from '../../utils/settings';
import { getProjectManagementPaths } from '../../modules/projectManagement/config';
import { canFeatureForRole, PERMISSIONS } from '../../utils/permissions';
import { useRolePermissionMatrix } from '../../hooks/useRolePermissionMatrix';
import { notifyApiError } from '../../utils/notify';
import { sortUsersByRole } from '../../utils/userSort';
import { getInitials } from '../../utils/getInitials';
import TaskReminderModal from '../../components/modals/TaskReminderModal';
import '../../styles/ProjectStatCards.css';

const PRIMARY_GRADIENT = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';

const TEAMS_PURPLE = 'rgb(102, 126, 234)';
const TEAMS_TOOLBAR_BORDER = '#dee2e6';

const TASK_STATUSES = ['Not Started', 'In Progress', 'R&D Development', 'R&D Testing', 'Final Verification', 'Complete', 'Re-open', 'On Hold', 'Invalid Issue'];
// Display labels for status values — 'Complete' is stored in DB but shown as 'Completed' in UI.
const STATUS_DISPLAY_LABEL = { Complete: 'Completed' };
const PRIORITIES = ['Low', 'Medium', 'High'];
const TASK_CATEGORIES = ['Requirement', 'Defect', 'Common Tasks'];

// Edit-task modal only lists a couple of attachments; the rest open on the task view page.
const VISIBLE_ATTACHMENT_LIMIT = 2;

// Max number of NEW files a user can upload at once. Only newly added (pending)
// files count toward this — existing attachments already on the task are ignored.
const MAX_NEW_ATTACHMENTS = 2;

// Accent colour per status (aligned with the app status variants / dashboard palette).
const TASK_STATUS_META = {
  'Not Started': { accent: '#64748b', tint: 'rgba(100, 116, 139, 0.14)' },
  'In Progress': { accent: '#3b82f6', tint: 'rgba(59, 130, 246, 0.14)' },
  'On Hold': { accent: '#f59e0b', tint: 'rgba(245, 158, 11, 0.16)' },
  'R&D Development': { accent: '#0891b2', tint: 'rgba(8, 145, 178, 0.14)' },
  'R&D Testing': { accent: '#6366f1', tint: 'rgba(99, 102, 241, 0.14)' },
  'Final Verification': { accent: '#ec4899', tint: 'rgba(236, 72, 153, 0.14)' },
  Complete: { accent: '#10b981', tint: 'rgba(16, 185, 129, 0.16)' },
  'Re-open': { accent: '#ef4444', tint: 'rgba(239, 68, 68, 0.14)' },
  'Invalid Issue': { accent: '#9333ea', tint: 'rgba(147, 51, 234, 0.14)' },
  // Legacy status kept so any not-yet-migrated task still renders coloured.
  Invalid: { accent: '#9333ea', tint: 'rgba(147, 51, 234, 0.14)' },
};
const TASK_DEADLINE_META = { accent: '#dc2626', tint: 'rgba(220, 38, 38, 0.14)' };
// Neutral accent for the "All Status" total card.
const TASK_ALL_STATUS_META = { accent: '#475569', tint: 'rgba(71, 85, 105, 0.12)' };
const initialTaskState = {
  projectId: '',
  featureId: '',
  name: '',
  public: false,
  billable: false,
  hourlyRate: 0,
  status: 'Not Started',
  priority: 'Medium',
  taskCategory: 'Requirement',
  startDate: '',
  dueDate: '',
  assignees: [],
  followers: [],
  hour: '',
  actualHour: '',
  taskDescription: '',
  // Scratch field only — not part of the task record. On save, non-empty text is posted to the
  // task's comment thread (same one shown on the task detail page) and then cleared.
  comment: '',
};


const getTaskAssigneeNames = (task) => {
  const detailed = Array.isArray(task?.assigneesDetail)
    ? task.assigneesDetail
      .map((person) => person?.name || person?.userName || person?.email || '')
      .map((value) => String(value || '').trim())
      .filter(Boolean)
    : [];
  if (detailed.length) return detailed;
  return String(task?.assignee || '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
};

const isCompletedTaskStatus = (status) => String(status || '').trim().toLowerCase() === 'complete';

const isTaskOverdue = (task) => {
  if (!task?.dueDate || task.dueDate === '-') return false;
  if (isCompletedTaskStatus(task.status)) return false;
  const dueDate = new Date(task.dueDate);
  if (Number.isNaN(dueDate.getTime())) return false;
  dueDate.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return dueDate < today;
};

const UserMultiSelectDropdown = ({
  label,
  placeholder,
  users,
  selectedIds,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  // Live mirror of the selection, read by the ordering memo below without being one of its deps.
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;

  const filteredUsers = useMemo(() => {
    const value = search.trim().toLowerCase();
    const list = !value
      ? users
      : users.filter((user) =>
          `${user.name || ''} ${user.email || ''} ${user.role || ''}`.toLowerCase().includes(value)
        );
    const sorted = sortUsersByRole(list);
    // Selected users float to the top so the current selection is always visible first. This order is
    // snapshotted when the dropdown opens or the search changes — NOT on every toggle — otherwise
    // ticking a checkbox reshuffles the rows and loses the user's scroll position mid-selection.
    const selectedSet = new Set((selectedIdsRef.current || []).map((id) => String(id)));
    const selected = sorted.filter((user) => selectedSet.has(String(user._id || user.id)));
    const rest = sorted.filter((user) => !selectedSet.has(String(user._id || user.id)));
    return [...selected, ...rest];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, search, open]);

  const selectedUsers = users.filter((user) => selectedIds.includes(String(user._id || user.id || '')));

  const toggleUser = (userId) => {
    const id = String(userId || '');
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((sid) => sid !== id));
      return;
    }
    onChange([...selectedIds, id]);
  };

  const filteredIds = filteredUsers.map((user) => String(user._id || user.id || ''));
  const allSelected = filteredIds.length > 0 && filteredIds.every((id) => selectedIds.includes(id));

  const toggleSelectAll = () => {
    if (allSelected) {
      onChange(selectedIds.filter((id) => !filteredIds.includes(id)));
    } else {
      onChange([...new Set([...selectedIds.map((id) => String(id)), ...filteredIds])]);
    }
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative', minWidth: 0 }}>
      <div style={fieldLabelStyle}>{label}</div>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        style={{
          ...inputControlStyle,
          width: '100%',
          minWidth: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px',
          cursor: 'pointer',
        }}
      >
        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selectedUsers.length > 0
            ? selectedUsers.map((user) => user.name).join(', ')
            : (placeholder || 'Nothing selected')}
        </span>
        <span style={{ flexShrink: 0, fontSize: '12px', color: '#718096' }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={userDropdownPanelStyle}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
            <input
              placeholder={`Search ${(label || 'users').toLowerCase()}...`}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              style={{ ...inputControlStyle, flex: 1, minWidth: 0 }}
            />
            {filteredUsers.length > 0 && (
              <button type="button" onClick={toggleSelectAll} style={{ ...selectAllBtnStyle, marginBottom: 0, whiteSpace: 'nowrap', flexShrink: 0 }}>
                {allSelected ? 'Deselect All' : 'Select All'}
              </button>
            )}
          </div>
          <div style={{ maxHeight: '160px', overflowY: 'auto', borderTop: '1px solid #edf2f7', paddingTop: '8px' }}>
            {filteredUsers.map((user) => {
              const userId = String(user._id || user.id || '');
              const checked = selectedIds.includes(userId);
              return (
                <label
                  key={userId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 2px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f7fafc',
                  }}
                >
                  <input type="checkbox" checked={checked} onChange={() => toggleUser(userId)} />
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: '13px', color: '#2d3748', fontWeight: 600 }}>
                      {user.name || 'Unnamed User'} ({user.role || '-'})
                    </span>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// sessionStorage key for persisting the Tasks list tab + filters across navigation (e.g. task detail → Back).
const FILTERS_STORAGE_KEY = 'tasksListFilters';

const TasksListPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const projectManagementPaths = getProjectManagementPaths(
    location.pathname.startsWith('/employee') ? 'employee' : 'admin'
  );
  const currentUserId = useMemo(() => {
    try {
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      return String(user?.id || user?._id || '').trim();
    } catch (_) {
      return '';
    }
  }, []);
  const currentUserName = useMemo(() => {
    try {
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      return String(user?.name || '').trim();
    } catch (_) {
      return '';
    }
  }, []);
  const currentUserRole = useMemo(() => {
    try {
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      return String(user?.role || '').trim().toLowerCase();
    } catch (_) {
      return '';
    }
  }, []);
  // isAdminView: true only for the /admin path (actual admin users)
  // hod/manager on /employee path get task-level edit based on assignee check, not blanket admin powers
  const isAdminView = useMemo(
    () => location.pathname.startsWith('/admin'),
    [location.pathname]
  );

  // Roles that can edit/delete tasks they are assigned to (broader than employee)
  const isManagerLikeRole = useMemo(
    () => ['admin', 'hod', 'manager'].includes(currentUserRole),
    [currentUserRole]
  );

  // Settings → User Roles matrix drives which action buttons show (matches backend enforcement).
  const { rolePermissions } = useRolePermissionMatrix();
  const canAddTask = canFeatureForRole(currentUserRole, rolePermissions, [PERMISSIONS.ADD_TASK]);
  const canEditTaskPerm = canFeatureForRole(currentUserRole, rolePermissions, [PERMISSIONS.EDIT_TASK]);
  const canDeleteTaskPerm = canFeatureForRole(currentUserRole, rolePermissions, [PERMISSIONS.DELETE_TASK]);
  // "View All Tasks" lets any role (incl. admin) see every user's tasks. Powers the All Users Tasks tab.
  const hasViewAllTasksPerm = canFeatureForRole(currentUserRole, rolePermissions, [PERMISSIONS.VIEW_ALL_TASKS]);

  // "All Users Tasks" tab is governed purely by the View All Tasks permission (matrix), for every role
  // including admin — so unchecking it in Settings actually hides the tab.
  const showAllUsersTasksTab = hasViewAllTasksPerm;

  // Roles that can see Team Tasks tab
  const canSeeTeamTab = useMemo(
    () => ['admin', 'hod', 'manager', 'team-leader'].includes(currentUserRole),
    [currentUserRole]
  );

  // All user IDs who report to the current user (direct + indirect via manager chain)
  // NOTE: `users` state is populated later, so this starts as [] and recomputes once users load.
  // It must be declared here (before useEffect deps reference it) to avoid "before initialization" errors.
  const [usersForHierarchy, setUsersForHierarchy] = useState([]);
  const subordinateIds = useMemo(() => {
    if (!currentUserId || !canSeeTeamTab) return [];
    // Walk the manager chain to collect direct + indirect reports (applies to admin too, so the
    // Team tab shows only the user's actual hierarchy — the "All Users Tasks" tab covers everyone).
    const result = [];
    const visited = new Set();
    const queue = [currentUserId];
    while (queue.length) {
      const managerId = queue.shift();
      if (visited.has(managerId)) continue;
      visited.add(managerId);
      usersForHierarchy.forEach((u) => {
        const uid = String(u._id || u.id || '');
        const managerRef = String(u.manager?._id || u.manager?.id || u.manager || '');
        if (managerRef === managerId && uid !== currentUserId) {
          result.push(uid);
          queue.push(uid);
        }
      });
    }
    return result;
  }, [usersForHierarchy, currentUserId, canSeeTeamTab]);

  // Tab + filters are persisted in sessionStorage so returning from a task detail (Back) restores them
  // instead of resetting to defaults. Read the last snapshot once, on mount.
  const savedFilters = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem(FILTERS_STORAGE_KEY) || '{}') || {};
    } catch (_) {
      return {};
    }
  }, []);

  // 'my' = My Tasks, 'following' = Following Tasks, 'team' = Team Tasks, 'all' = All Users Tasks.
  const [activeTab, setActiveTab] = useState(() => {
    const allowed = ['my', 'following', ...(canSeeTeamTab ? ['team'] : []), ...(showAllUsersTasksTab ? ['all'] : [])];
    return allowed.includes(savedFilters.activeTab) ? savedFilters.activeTab : 'my';
  });

  // If the active tab is no longer permitted (e.g. View All Tasks was revoked in Settings), fall back to My Tasks.
  useEffect(() => {
    const allowed = ['my', 'following', ...(canSeeTeamTab ? ['team'] : []), ...(showAllUsersTasksTab ? ['all'] : [])];
    if (!allowed.includes(activeTab)) {
      setActiveTab('my');
    }
  }, [canSeeTeamTab, showAllUsersTasksTab, activeTab]);
  const [projects, setProjects] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [users, setUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [summaryTasks, setSummaryTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalTasks, setTotalTasks] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState(savedFilters.search ?? '');
  const [statusFilter, setStatusFilter] = useState(savedFilters.statusFilter ?? []);
  const [priorityFilter, setPriorityFilter] = useState(savedFilters.priorityFilter ?? []);
  const [projectFilter, setProjectFilter] = useState(Array.isArray(savedFilters.projectFilter) ? savedFilters.projectFilter : []);
  const [memberFilter, setMemberFilter] = useState(savedFilters.memberFilter ?? (currentUserId || 'All'));
  // Team Tasks tab: [] = all subordinates, or a set of specific subordinate IDs
  const [teamMemberFilter, setTeamMemberFilter] = useState(Array.isArray(savedFilters.teamMemberFilter) ? savedFilters.teamMemberFilter : []);
  const [overdueOnly, setOverdueOnly] = useState(savedFilters.overdueOnly ?? false);
  // Column sort, applied server-side across all pages. Empty sortBy = default (newest first).
  const [sort, setSort] = useState(savedFilters.sort?.sortBy ? savedFilters.sort : { sortBy: '', sortOrder: 'asc' });
  // Drives the Reset Filters button: red while something the button would clear is set, grey/disabled otherwise.
  const hasActiveFilters =
    Boolean(search.trim()) ||
    (Array.isArray(statusFilter) ? statusFilter.length > 0 : statusFilter !== 'All') ||
    (Array.isArray(priorityFilter) ? priorityFilter.length > 0 : priorityFilter !== 'All') ||
    projectFilter.length > 0 ||
    memberFilter !== (currentUserId || 'All') ||
    teamMemberFilter.length > 0 ||
    overdueOnly;
  const [currentPage, setCurrentPage] = useState(1);
  // Page size follows General Settings (not the persisted filter snapshot), same as the People module.
  const [itemsPerPage, setItemsPerPage] = useState(getDefaultItemsPerPageSync());
  const [viewMode, setViewMode] = useState(savedFilters.viewMode ?? 'list');
  const [kanbanTasks, setKanbanTasks] = useState([]);
  const [kanbanLoading, setKanbanLoading] = useState(false);
  const [draggedTaskId, setDraggedTaskId] = useState('');
  const [movingTaskId, setMovingTaskId] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [savingTask, setSavingTask] = useState(false);
  const [deletingTaskId, setDeletingTaskId] = useState('');
  const [confirmDeleteTaskId, setConfirmDeleteTaskId] = useState('');
  const [remindingTaskId, setRemindingTaskId] = useState('');
  const [reminderTask, setReminderTask] = useState(null);
  const [editingTaskId, setEditingTaskId] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [newTask, setNewTask] = useState(initialTaskState);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [removingAttachmentId, setRemovingAttachmentId] = useState('');

  useEffect(() => {
    loadProjectsAndUsers();
  }, []);

  useEffect(() => {
    loadSummaryTasks();
  }, [search, priorityFilter, projectFilter, memberFilter, teamMemberFilter, projectOptions, activeTab, subordinateIds]);

  useEffect(() => {
    loadTasks();
  }, [currentPage, itemsPerPage, search, statusFilter, priorityFilter, projectFilter, memberFilter, teamMemberFilter, projectOptions, activeTab, subordinateIds, sort]);

  useEffect(() => {
    if (viewMode !== 'kanban') return;
    loadKanbanTasks();
  }, [viewMode, search, statusFilter, priorityFilter, projectFilter, memberFilter, teamMemberFilter, projectOptions, activeTab, subordinateIds]);

  // Fetch settings on mount and update itemsPerPage if needed
  useEffect(() => {
    const updateItemsPerPage = async () => {
      const defaultItems = await getDefaultItemsPerPage();
      if (defaultItems !== itemsPerPage) {
        setItemsPerPage(defaultItems);
      }
    };
    updateItemsPerPage();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, priorityFilter, projectFilter, memberFilter, teamMemberFilter, activeTab, sort]);

  // Persist tab + filters so they survive navigation (task detail → Back) and page refresh.
  useEffect(() => {
    const snapshot = {
      activeTab,
      search,
      statusFilter,
      priorityFilter,
      projectFilter,
      memberFilter,
      teamMemberFilter,
      overdueOnly,
      viewMode,
      sort,
    };
    try {
      sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(snapshot));
    } catch (_) {
      /* ignore storage write failures */
    }
  }, [activeTab, search, statusFilter, priorityFilter, projectFilter, memberFilter, teamMemberFilter, overdueOnly, viewMode, sort]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages || 1);
    }
  }, [currentPage, totalPages]);

  const loadProjectsAndUsers = async () => {
    try {
      const firstPage = await api.getProjects({ page: 1, limit: 200 });
      const firstPageRows = firstPage?.data || [];
      let allProjects = [...firstPageRows];
      const fetchedTotalPages = Math.max(1, firstPage?.totalPages || 1);

      if (fetchedTotalPages > 1) {
        const requests = [];
        for (let page = 2; page <= fetchedTotalPages; page += 1) {
          requests.push(api.getProjects({ page, limit: 200 }));
        }
        const responses = await Promise.all(requests);
        responses.forEach((response) => {
          if (response?.success) allProjects = allProjects.concat(response.data || []);
        });
      }

      setProjects(allProjects);
      setProjectOptions(allProjects);
      const userResponse = await api.getUsers({ limit: 1000, isActive: 'true' });
      if (userResponse?.success) {
        const allUsers = userResponse.data || [];
        setUsers(allUsers);
        setUsersForHierarchy(allUsers);
      }
    } catch (error) {
      notifyApiError(error, 'Failed to load task metadata');
    }
  };

  const loadTasks = async () => {
    try {
      setLoading(true);
      const selectedProjectIds = projectOptions.filter((project) => projectFilter.includes(project.name)).map((project) => project._id);
      const isTeamTab = activeTab === 'team' && canSeeTeamTab;
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: search || undefined,
        status: Array.isArray(statusFilter) && statusFilter.length > 0 ? statusFilter.join(',') : undefined,
        priority: Array.isArray(priorityFilter) && priorityFilter.length > 0 ? priorityFilter.join(',') : undefined,
        projectId: selectedProjectIds.length > 0 ? selectedProjectIds.join(',') : undefined,
        sortBy: sort.sortBy || undefined,
        sortOrder: sort.sortBy ? sort.sortOrder : undefined,
      };
      if (isTeamTab) {
        if (subordinateIds.length === 0) {
          setTasks([]);
          setTotalTasks(0);
          setTotalPages(1);
          setLoading(false);
          return;
        }
        // Selected team members, or all subordinates when none are picked
        params.teamMemberIds = teamMemberFilter.length > 0 ? teamMemberFilter.join(',') : subordinateIds.join(',');
      } else if (activeTab === 'all' && showAllUsersTasksTab) {
        // All Users Tasks — everyone's tasks, or selected users' when some are picked. Filter by
        // "Assigned To" (assignee/assignedTo) only — NOT followers.
        if (teamMemberFilter.length > 0) {
          params.assigneeId = teamMemberFilter.join(',');
        }
      } else if (activeTab === 'following') {
        // Following Tasks — tasks I follow (not necessarily assigned to me)
        params.followerId = currentUserId || undefined;
      } else {
        // My Tasks tab — tasks assigned to me (assignee/assignedTo), excluding follow-only
        params.assigneeId = currentUserId || undefined;
      }
      const response = await api.getTasks(params);
      if (response?.success) {
        setTasks(response.data || []);
        setTotalTasks(response.total || 0);
        setTotalPages(response.totalPages || 1);
      }
    } catch (error) {
      notifyApiError(error, 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const buildSummaryQueryParams = (override = {}) => {
    const selectedProjectIds = projectOptions.filter((project) => projectFilter.includes(project.name)).map((project) => project._id);
    const isTeamTab = activeTab === 'team' && canSeeTeamTab;
    const base = {
      search: search || undefined,
      priority: priorityFilter !== 'All' ? priorityFilter : undefined,
      projectId: selectedProjectIds.length > 0 ? selectedProjectIds.join(',') : undefined,
    };
    if (isTeamTab) {
      if (subordinateIds.length === 0) {
        // No subordinates — caller should bail out before using these params
        base._noTeamMembers = true;
      } else {
        base.teamMemberIds = teamMemberFilter.length > 0 ? teamMemberFilter.join(',') : subordinateIds.join(',');
      }
    } else if (activeTab === 'all' && showAllUsersTasksTab) {
      // All Users Tasks — everyone's tasks, or selected users' when some are picked. Filter by
      // "Assigned To" (assignee/assignedTo) only — NOT followers.
      if (teamMemberFilter.length > 0) {
        base.assigneeId = teamMemberFilter.join(',');
      }
    } else if (activeTab === 'following') {
      base.followerId = currentUserId || undefined;
    } else {
      base.assigneeId = currentUserId || undefined;
    }
    return { ...base, ...override };
  };

  const loadSummaryTasks = async () => {
    // On Team Tasks tab with no subordinates — show zeros, don't fetch
    if (activeTab === 'team' && canSeeTeamTab && subordinateIds.length === 0) {
      setSummaryTasks([]);
      return;
    }
    try {
      let page = 1;
      const limit = 200;
      let totalPagesLocal = 1;
      const allRows = [];
      do {
        const response = await api.getTasks(buildSummaryQueryParams({ page, limit }));
        if (!response?.success) break;
        allRows.push(...(response.data || []));
        totalPagesLocal = Math.max(1, response.totalPages || 1);
        page += 1;
      } while (page <= totalPagesLocal);
      setSummaryTasks(allRows);
    } catch (_) {
      // Keep page usable even if summary data fails.
    }
  };

  const buildTaskQueryParams = (override = {}) => {
    const selectedProjectIds = projectOptions.filter((project) => projectFilter.includes(project.name)).map((project) => project._id);
    const isTeamTab = activeTab === 'team' && canSeeTeamTab;
    const base = {
      search: search || undefined,
      status: Array.isArray(statusFilter) && statusFilter.length > 0 ? statusFilter.join(',') : undefined,
      priority: Array.isArray(priorityFilter) && priorityFilter.length > 0 ? priorityFilter.join(',') : undefined,
      projectId: selectedProjectIds.length > 0 ? selectedProjectIds.join(',') : undefined,
    };
    if (isTeamTab) {
      if (subordinateIds.length > 0) {
        base.teamMemberIds = teamMemberFilter.length > 0 ? teamMemberFilter.join(',') : subordinateIds.join(',');
      }
    } else if (activeTab === 'all' && showAllUsersTasksTab) {
      // All Users Tasks — everyone's tasks, or selected users' when some are picked. Filter by
      // "Assigned To" (assignee/assignedTo) only — NOT followers.
      if (teamMemberFilter.length > 0) {
        base.assigneeId = teamMemberFilter.join(',');
      }
    } else if (activeTab === 'following') {
      base.followerId = currentUserId || undefined;
    } else {
      base.assigneeId = currentUserId || undefined;
    }
    return { ...base, ...override };
  };

  const loadKanbanTasks = async () => {
    try {
      setKanbanLoading(true);
      let page = 1;
      const limit = 200;
      let totalPagesLocal = 1;
      const allRows = [];

      do {
        const response = await api.getTasks(buildTaskQueryParams({ page, limit }));
        if (!response?.success) break;
        allRows.push(...(response.data || []));
        totalPagesLocal = Math.max(1, response.totalPages || 1);
        page += 1;
      } while (page <= totalPagesLocal);

      setKanbanTasks(allRows);
    } catch (error) {
      notifyApiError(error, 'Failed to load board tasks');
    } finally {
      setKanbanLoading(false);
    }
  };

  const moveTaskToStatus = async (taskId, targetStatus) => {
    const id = String(taskId || '');
    if (!id || !targetStatus) return;

    const sourceTask = kanbanTasks.find((task) => String(task.id) === id);
    if (!sourceTask || sourceTask.status === targetStatus) return;

    const prevTasks = kanbanTasks;
    const prevSummary = summaryTasks;
    setKanbanTasks((prev) =>
      prev.map((task) =>
        String(task.id) === id
          ? { ...task, status: targetStatus }
          : task
      )
    );
    // Keep the summary cards (status counts + overdue) in sync instantly.
    setSummaryTasks((prev) =>
      prev.map((task) => (String(task.id) === id ? { ...task, status: targetStatus } : task))
    );
    setMovingTaskId(id);

    try {
      await api.updateTask(id, { status: targetStatus });
      toast.success(`Task moved to ${targetStatus}`);
      setTasks((prev) =>
        prev.map((task) => (String(task.id) === id ? { ...task, status: targetStatus } : task))
      );
    } catch (error) {
      setKanbanTasks(prevTasks);
      setSummaryTasks(prevSummary);
      toast.error(error.message || 'Failed to move task');
    } finally {
      setMovingTaskId('');
    }
  };

  const updateTaskStatusFromTable = async (task, targetStatus) => {
    const id = String(task?.id || '');
    if (!id || !targetStatus || task.status === targetStatus) return;

    const prevTasks = tasks;
    const prevKanban = kanbanTasks;
    const prevSummary = summaryTasks;
    setTasks((prev) =>
      prev.map((row) => (String(row.id) === id ? { ...row, status: targetStatus } : row))
    );
    setKanbanTasks((prev) =>
      prev.map((row) => (String(row.id) === id ? { ...row, status: targetStatus } : row))
    );
    // Keep the summary cards (status counts + overdue) in sync instantly.
    setSummaryTasks((prev) =>
      prev.map((row) => (String(row.id) === id ? { ...row, status: targetStatus } : row))
    );
    setMovingTaskId(id);

    try {
      await api.updateTask(id, { status: targetStatus });
      toast.success(`Task moved to ${targetStatus}`);
    } catch (error) {
      setTasks(prevTasks);
      setKanbanTasks(prevKanban);
      setSummaryTasks(prevSummary);
      toast.error(error.message || 'Failed to update task status');
    } finally {
      setMovingTaskId('');
    }
  };

  // Inline priority change from the table (mirrors the inline status dropdown) — optimistic with rollback.
  const updateTaskPriorityFromTable = async (task, targetPriority) => {
    const id = String(task?.id || '');
    if (!id || !targetPriority || task.priority === targetPriority) return;

    const prevTasks = tasks;
    const prevKanban = kanbanTasks;
    setTasks((prev) =>
      prev.map((row) => (String(row.id) === id ? { ...row, priority: targetPriority } : row))
    );
    setKanbanTasks((prev) =>
      prev.map((row) => (String(row.id) === id ? { ...row, priority: targetPriority } : row))
    );
    setMovingTaskId(id);

    try {
      await api.updateTask(id, { priority: targetPriority });
      toast.success(`Priority set to ${targetPriority}`);
    } catch (error) {
      setTasks(prevTasks);
      setKanbanTasks(prevKanban);
      toast.error(error.message || 'Failed to update task priority');
    } finally {
      setMovingTaskId('');
    }
  };

  const uniqueProjects = useMemo(
    () => ['All', ...Array.from(new Set(projectOptions.map((project) => project.name)))],
    [projectOptions]
  );

  const memberOptions = useMemo(() => {
    const base = (users || [])
      .map((user) => ({
        id: String(user._id || user.id || ''),
        name: String(user.name || '').trim(),
      }))
      .filter((user) => user.id && user.name)
      .sort((a, b) => a.name.localeCompare(b.name));
    const hasCurrentUser = !!currentUserId && base.some((user) => user.id === currentUserId);
    const fallbackCurrent = !hasCurrentUser && currentUserId
      ? [{ id: currentUserId, name: currentUserName || 'My Tasks' }]
      : [];
    return [{ id: 'All', name: 'All Members' }, ...fallbackCurrent, ...base];
  }, [users, currentUserId, currentUserName]);

  // All user IDs who report to the current user (direct + indirect via manager chain)
  // (computed above using usersForHierarchy state, which is synced from users in loadProjectsAndUsers)

  const summary = useMemo(() => {
    const sourceTasks = summaryTasks;
    const counts = TASK_STATUSES.reduce((acc, status) => ({ ...acc, [status]: 0 }), {});
    sourceTasks.forEach((task) => {
      // Legacy 'Invalid' tasks count under the renamed 'Invalid Issue' bucket.
      const status = task.status === 'Invalid' ? 'Invalid Issue' : task.status;
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [summaryTasks]);

  const overdueTasksCount = useMemo(
    () => summaryTasks.reduce((count, task) => (isTaskOverdue(task) ? count + 1 : count), 0),
    [summaryTasks]
  );

  const visibleTasks = useMemo(
    () => (overdueOnly ? tasks.filter((task) => isTaskOverdue(task)) : tasks),
    [tasks, overdueOnly]
  );

  const visibleKanbanTasks = useMemo(
    () => (overdueOnly ? kanbanTasks.filter((task) => isTaskOverdue(task)) : kanbanTasks),
    [kanbanTasks, overdueOnly]
  );

  const eligibleProjectUsers = useMemo(() => {
    if (!newTask.projectId) return users;
    const selectedProject = projects.find((project) => String(project._id) === String(newTask.projectId));
    if (!selectedProject) return users;

    const memberIds = new Set((selectedProject.members || []).map((member) => String(member?._id || member?.id)));
    if (!memberIds.size) return users;
    return users.filter((user) => memberIds.has(String(user._id || user.id)));
  }, [newTask.projectId, projects, users]);

  const selectedProjectFeatures = useMemo(() => {
    if (!newTask.projectId) return [];
    const selectedProject = projects.find((project) => String(project._id) === String(newTask.projectId));
    return Array.isArray(selectedProject?.features) ? selectedProject.features : [];
  }, [newTask.projectId, projects]);

  // Assignees cleanup: strip assignees that are no longer members of the newly selected project
  useEffect(() => {
    if (!newTask.projectId) {
      return;
    }

    const allowedIds = new Set(eligibleProjectUsers.map((user) => String(user._id || user.id)));
    const nextAssignees = newTask.assignees.filter((id) => allowedIds.has(String(id)));

    if (nextAssignees.length !== newTask.assignees.length) {
      setNewTask((prev) => ({
        ...prev,
        assignees: nextAssignees,
      }));
    }
  }, [newTask.projectId, newTask.assignees, eligibleProjectUsers]);

  // Followers auto-seed: when project is selected in Add mode, pre-select project-level followers
  // We track the previous projectId to only act when it actually changes.
  const prevProjectIdRef = useRef(null);
  useEffect(() => {
    const prev = prevProjectIdRef.current;
    const next = newTask.projectId;
    prevProjectIdRef.current = next;

    // Only run in Add mode (editingTaskId is empty)
    if (editingTaskId) return;

    // No project selected — nothing to seed (leave followers as-is)
    if (!next) return;

    // Project didn't actually change — skip (prevents re-seeding on unrelated re-renders)
    if (next === prev) return;

    // Project changed to a new non-empty value — seed followers from the selected project
    const selectedProject = projects.find((p) => String(p._id) === next);
    const projectFollowerIds = (selectedProject?.followers || [])
      .map((f) => String(f?._id || f?.id || f || ''))
      .filter(Boolean);
    setNewTask((prev) => ({ ...prev, followers: projectFollowerIds }));
  }, [newTask.projectId, editingTaskId, projects]);

  useEffect(() => {
    if (!newTask.projectId) {
      if (newTask.featureId) {
        setNewTask((prev) => ({ ...prev, featureId: '' }));
      }
      return;
    }
    if (!newTask.featureId) return;
    const exists = selectedProjectFeatures.some(
      (feature) => String(feature?._id || feature?.id || '') === String(newTask.featureId)
    );
    if (!exists) {
      setNewTask((prev) => ({ ...prev, featureId: '' }));
    }
  }, [newTask.projectId, newTask.featureId, selectedProjectFeatures]);

  const openAddTaskModal = () => {
    prevProjectIdRef.current = null; // reset so next project selection triggers the auto-seed
    setEditingTaskId('');
    setNewTask(initialTaskState);
    setPendingAttachments([]);
    setExistingAttachments([]);
    setShowAddModal(true);
  };

  const handleEditTask = async (taskId) => {
    try {
      setSavingTask(true);
      const response = await api.getTask(taskId);
      const task = response?.data?.task;
      const project = response?.data?.project;
      if (!task) {
        toast.error('Task details not found');
        return;
      }
      setEditingTaskId(taskId);
      const assigneeIds = (task.assignees || [])
        .map((u) => String(u?._id || u?.id || u?.userId || u || '').trim())
        .filter(Boolean);
      const ownActualRow = (task.actualHoursByUser || []).find(
        (entry) => String(entry?.user?._id || entry?.user || '') === String(currentUserId || '')
      );
      const ownActualHour = Number(ownActualRow?.hour || 0);
      const isCurrentUserAssignee =
        assigneeIds.includes(String(currentUserId || '')) || Boolean(ownActualRow);
      setNewTask({
        projectId: String(project?._id || ''),
        featureId: String(task.featureId?._id || task.featureId || ''),
        name: task.name || '',
        public: Boolean(task.public),
        billable: Boolean(task.billable),
        hourlyRate: task.hourlyRate || 0,
        status: task.status || 'Not Started',
        priority: task.priority || 'Medium',
        taskCategory: task.taskCategory || 'Requirement',
        startDate: task.startDate ? new Date(task.startDate).toISOString().slice(0, 10) : '',
        dueDate: task.dueDate ? new Date(task.dueDate).toISOString().slice(0, 10) : '',
        assignees: (task.assignees || []).map((u) => String(u?._id || u?.id || u || '')).filter(Boolean),
        // Use the task's own saved followers exactly as stored. Do NOT fall back to
        // project-level followers here — an intentional "no followers" state (empty array)
        // must be preserved when re-opening the edit modal, otherwise they get re-seeded.
        // New-task follower seeding for the Add flow is handled separately by the
        // prevProjectIdRef effect above (Add mode only).
        followers: (task.followers || [])
          .map((u) => String(u?._id || u?.id || u || ''))
          .filter(Boolean),
        hour: task.hour || '',
        actualHour:
          !isAdminView && !isManagerLikeRole
            ? (isCurrentUserAssignee ? ownActualHour : Number(task.actualHour || 0))
            : (task.actualHour || ''),
        taskDescription: task.taskDescription || '',
        comment: '',
      });
      setPendingAttachments([]);
      setExistingAttachments(task.attachments || []);
      setShowAddModal(true);
    } catch (error) {
      notifyApiError(error, 'Failed to load task details');
    } finally {
      setSavingTask(false);
    }
  };

  const handleRemoveExistingAttachment = async (attachmentId) => {
    if (!attachmentId || !editingTaskId) return;
    const confirmed = window.confirm('Remove this attachment?');
    if (!confirmed) return;
    try {
      setRemovingAttachmentId(String(attachmentId));
      await api.deleteTaskAttachment(editingTaskId, attachmentId);
      toast.success('Attachment removed');
      setExistingAttachments((prev) => prev.filter((file) => String(file._id) !== String(attachmentId)));
      loadTasks();
    } catch (error) {
      toast.error(error.message || 'Failed to remove attachment');
    } finally {
      setRemovingAttachmentId('');
    }
  };

  const handleSubmitTask = async () => {
    if (!newTask.name.trim()) {
      toast.warning('Task Name is required');
      return;
    }

    if (newTask.startDate && newTask.dueDate) {
      const start = new Date(newTask.startDate);
      const due = new Date(newTask.dueDate);
      start.setHours(0, 0, 0, 0);
      due.setHours(0, 0, 0, 0);
      if (!Number.isNaN(start.getTime()) && !Number.isNaN(due.getTime()) && due < start) {
        toast.warning('Due date cannot be before the start date');
        return;
      }
    }

    try {
      setSavingTask(true);
      const payload = {
        name: newTask.name.trim(),
        projectId: newTask.projectId,
        public: newTask.public,
        billable: newTask.billable,
        hourlyRate: Number(newTask.hourlyRate || 0),
        status: newTask.status,
        priority: newTask.priority,
        taskCategory: newTask.taskCategory,
        startDate: newTask.startDate || undefined,
        dueDate: newTask.dueDate || undefined,
        assignees: newTask.assignees,
        assignedTo: newTask.assignees[0] || undefined,
        followers: newTask.followers,
        featureId: newTask.featureId,
        hour: Number(newTask.hour || 0),
        taskDescription: newTask.taskDescription,
      };
      if (editingTaskId && !isAdminView && !isManagerLikeRole) {
        const isCurrentUserAssignee = (newTask.assignees || []).some(
          (userId) => String(userId) === String(currentUserId || '')
        );
        if (isCurrentUserAssignee) {
          payload.actualHourScope = 'self';
          payload.actualHour = Number(newTask.actualHour || 0);
        }
      }
      if (!editingTaskId) {
        payload.actualHour = Number(newTask.actualHour || 0);
      }

      let taskIdForAttachments = editingTaskId;
      if (editingTaskId) {
        await api.updateTask(editingTaskId, payload);
        toast.success('Task updated successfully');
      } else {
        let response;
        if (newTask.projectId) {
          response = await api.addProjectTask(newTask.projectId, payload);
        } else {
          response = await api.addTask(payload);
        }
        taskIdForAttachments = String(response?.data?._id || response?.data?.task?._id || '');
        toast.success('Task added successfully');
      }

      if (pendingAttachments.length > 0 && taskIdForAttachments) {
        try {
          await api.uploadTaskAttachments(taskIdForAttachments, pendingAttachments);
        } catch (uploadError) {
          toast.error(uploadError.message || 'Task saved, but attachment upload failed');
        }
      }

      if ((newTask.comment || '').trim() && taskIdForAttachments) {
        try {
          await api.addTaskComment(taskIdForAttachments, newTask.comment.trim());
        } catch (commentError) {
          toast.error(commentError.message || 'Task saved, but comment could not be added');
        }
      }

      setShowAddModal(false);
      setEditingTaskId('');
      setNewTask(initialTaskState);
      setPendingAttachments([]);
      setExistingAttachments([]);
      loadTasks();
    } catch (error) {
      toast.error(error.message || `Failed to ${editingTaskId ? 'update' : 'add'} task`);
    } finally {
      setSavingTask(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!taskId) return;
    try {
      setDeletingTaskId(taskId);
      await api.deleteTask(taskId);
      toast.success('Task moved to trash');
      setTasks((prev) => prev.filter((task) => task.id !== taskId));
      setConfirmDeleteTaskId('');
    } catch (error) {
      toast.error(error.message || 'Failed to delete task');
    } finally {
      setDeletingTaskId('');
    }
  };

  // Open the reminder composer for a task (edit message + pick recipients).
  const handleSendReminder = (task) => {
    if (!task?.id) return;
    setReminderTask(task);
  };

  // Normalise a task's assignees into { id, name, email } for the reminder modal.
  const getReminderAssignees = (task) => {
    const detail = Array.isArray(task?.assigneesDetail) ? task.assigneesDetail : [];
    const map = new Map();
    detail.forEach((a) => {
      const id = String(a?.userId || a?._id || a?.id || '');
      if (id) map.set(id, { id, name: a.name || a.email || 'User', email: a.email || '' });
    });
    return [...map.values()];
  };

  const submitReminder = async ({ note, recipientIds }) => {
    const taskId = reminderTask?.id;
    if (!taskId) return;
    try {
      setRemindingTaskId(taskId);
      const res = await api.sendTaskReminder(taskId, { note, recipientIds });
      toast.success(res?.message || 'Reminder sent');
      setReminderTask(null);
    } catch (error) {
      toast.error(error.message || 'Failed to send reminder');
    } finally {
      setRemindingTaskId('');
    }
  };

  // Edit is governed purely by the Edit Task permission (matches the backend, which checks
  // edit_task by role — not whether you're an assignee). So anyone granted edit_task can edit
  // any task, and assignees who lack it cannot (they'd get a 403 anyway).
  const canEditTask = () => canEditTaskPerm;

  // Delete requires the Delete Task permission from the User Roles matrix.
  const canDeleteTask = () => canDeleteTaskPerm;

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const toCsv = (rows) => {
    const headers = ['Task ID', 'Task Name', 'Project', 'Status', 'Start Date', 'Due Date', 'Assignees', 'Priority'];
    const body = rows.map((row) => [
      row.taskId,
      row.taskName,
      row.project,
      row.status,
      row.startDate,
      row.dueDate,
      row.assignees,
      row.priority,
    ]);
    return [headers, ...body]
      .map((line) => line.map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
  };

  const downloadFile = (content, mimeType, fileName) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const openPrintPreview = (html) => {
    const popup = window.open('', '_blank', 'width=1100,height=700');
    if (!popup) {
      toast.error('Popup blocked. Please allow popups to print/export PDF.');
      return;
    }
    popup.document.open();
    popup.document.write(html);
    popup.document.close();
    popup.focus();
  };

  const buildExportRows = () => {
    const sourceTasks = viewMode === 'kanban' ? visibleKanbanTasks : visibleTasks;
    return sourceTasks.map((task) => {
      const assigneeNames = (task.assigneesDetail || [])
        .map((person) => person?.name || person?.userName || '')
        .filter(Boolean)
        .join(', ');
      return {
        taskId: task.taskCode || `T${String(task.id || '').slice(-4).toUpperCase()}`,
        taskName: task.title || '-',
        project: task.project || '-',
        status: task.status || '-',
        startDate: task.startDate || '-',
        dueDate: task.dueDate || '-',
        assignees: assigneeNames || task.assignee || '-',
        priority: task.priority || '-',
      };
    });
  };

  const handleExport = (type) => {
    setShowExportMenu(false);
    const rows = buildExportRows();
    if (!rows.length) {
      toast.info('No tasks to export');
      return;
    }

    const timestamp = new Date().toISOString().slice(0, 10);
    const csv = toCsv(rows);

    if (type === 'csv') {
      downloadFile(`\uFEFFsep=,\n${csv}`, 'text/csv;charset=utf-8;', `tasks-${timestamp}.csv`);
      return;
    }

    if (type === 'excel') {
      const excelRows = rows
        .map(
          (row) => `<tr>
  <td>${escapeHtml(row.taskId)}</td>
  <td>${escapeHtml(row.taskName)}</td>
  <td>${escapeHtml(row.project)}</td>
  <td>${escapeHtml(row.status)}</td>
  <td>${escapeHtml(row.startDate)}</td>
  <td>${escapeHtml(row.dueDate)}</td>
  <td>${escapeHtml(row.assignees)}</td>
  <td>${escapeHtml(row.priority)}</td>
</tr>`
        )
        .join('');
      const excelHtml = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <style>
    table { border-collapse: collapse; width: 100%; }
    th, td { border: 1px solid #cbd5e0; padding: 6px; font-size: 12px; text-align: left; }
    th { background: #edf2f7; font-weight: 700; }
  </style>
</head>
<body>
  <table>
    <thead>
      <tr>
        <th>Task ID</th><th>Task Name</th><th>Project</th><th>Status</th>
        <th>Start Date</th><th>Due Date</th><th>Assignees</th><th>Priority</th>
      </tr>
    </thead>
    <tbody>${excelRows}</tbody>
  </table>
</body>
</html>`;
      downloadFile(excelHtml, 'application/vnd.ms-excel;charset=utf-8;', `tasks-${timestamp}.xls`);
      return;
    }

    const tableRows = rows
      .map(
        (row) => `<tr>
  <td>${escapeHtml(row.taskId)}</td>
  <td>${escapeHtml(row.taskName)}</td>
  <td>${escapeHtml(row.project)}</td>
  <td>${escapeHtml(row.status)}</td>
  <td>${escapeHtml(row.startDate)}</td>
  <td>${escapeHtml(row.dueDate)}</td>
  <td>${escapeHtml(row.assignees)}</td>
  <td>${escapeHtml(row.priority)}</td>
</tr>`
      )
      .join('');

    const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Tasks Export</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 20px; }
    h2 { margin: 0 0 12px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #ccc; padding: 8px; font-size: 12px; text-align: left; }
    th { background: #f3f4f6; }
  </style>
</head>
<body>
  <h2>Tasks Export (${timestamp})</h2>
  <table>
    <thead>
      <tr>
        <th>Task ID</th><th>Task Name</th><th>Project</th><th>Status</th>
        <th>Start Date</th><th>Due Date</th><th>Assignees</th><th>Priority</th>
      </tr>
    </thead>
    <tbody>${tableRows}</tbody>
  </table>
</body>
</html>`;

    if (type === 'pdf') {
      openPrintPreview(html);
      return;
    }
    if (type === 'print') {
      const popup = window.open('', '_blank', 'width=1100,height=700');
      if (!popup) {
        toast.error('Popup blocked. Please allow popups to print.');
        return;
      }
      popup.document.open();
      popup.document.write(html);
      popup.document.close();
      popup.focus();
      popup.print();
      return;
    }
  };

  return (
    <div style={{ padding: '16px' }}>
      {/* ── Tabs: My Tasks / Following Tasks / Team Tasks / All Users Tasks ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', borderBottom: '2px solid #e2e8f0', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
        <div style={{ display: 'flex' }}>
          {[
            { key: 'my',        icon: '👤', label: 'My Tasks' },
            { key: 'following', icon: '🔖', label: 'Following Tasks' },
            ...(canSeeTeamTab ? [{ key: 'team', icon: '👥', label: 'Team Tasks' }] : []),
            ...(showAllUsersTasksTab ? [{ key: 'all', icon: '🌐', label: 'All Users Tasks' }] : []),
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => {
                setActiveTab(tab.key);
                setCurrentPage(1);
                setOverdueOnly(false);
                setStatusFilter([]);
                // Clear the per-member filter so it never carries over between Team / All Users tabs.
                setTeamMemberFilter([]);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '12px 18px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: activeTab === tab.key ? 700 : 600,
                color: activeTab === tab.key ? '#667eea' : '#718096',
                borderBottom: activeTab === tab.key ? '2px solid #667eea' : '2px solid transparent',
                marginBottom: '-2px',
                transition: 'color 0.15s',
                ...(activeTab === tab.key
                  ? {
                      background: PRIMARY_GRADIENT,
                      WebkitBackgroundClip: 'text',
                      backgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }
                  : {}),
              }}
            >
              <span style={{ WebkitTextFillColor: 'initial' }}>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
        {activeTab === 'team' && (
          <span style={{ fontSize: '12px', color: '#718096', padding: '8px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ background: '#ebf4ff', color: '#3182ce', borderRadius: '50%', width: '18px', height: '18px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700 }}>i</span>
            Showing tasks of your team members based on your hierarchy
          </span>
        )}
      </div>

      {/* ── Status summary cards ── */}
      <div className="pj-stat-grid" style={{ '--cols': TASK_STATUSES.length + 2 }}>
        {/* All Status — total tasks (first card). */}
        <div
          onClick={() => {
            setOverdueOnly(false);
            setStatusFilter([]);
            setCurrentPage(1);
          }}
          className={`pj-stat-card${Array.isArray(statusFilter) && statusFilter.length === 0 && !overdueOnly ? ' is-active' : ''}`}
          style={{ '--accent': TASK_ALL_STATUS_META.accent, '--tint': TASK_ALL_STATUS_META.tint }}
        >
          <div className="pj-stat-top">
            <span className="pj-stat-label">All Status</span>
          </div>
          <div className="pj-stat-value">{summaryTasks.length}</div>
        </div>
        {TASK_STATUSES.map((status) => {
          const meta = TASK_STATUS_META[status] || TASK_STATUS_META['Not Started'];
          const displayStatus = STATUS_DISPLAY_LABEL[status] || status;
          const isActive = Array.isArray(statusFilter) && statusFilter.includes(status) && !overdueOnly;
          return (
            <div
              key={status}
              onClick={(e) => {
                if (e.target.type === 'checkbox') return;
                setOverdueOnly(false);
                setStatusFilter((prev) => {
                  const arr = Array.isArray(prev) ? prev : [];
                  if (arr.includes(status)) {
                    return arr.filter((s) => s !== status);
                  }
                  return [...arr, status];
                });
                setCurrentPage(1);
              }}
              className={`pj-stat-card${isActive ? ' is-active' : ''}`}
              style={{ '--accent': meta.accent, '--tint': meta.tint }}
            >
              <div className="pj-stat-top" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => {
                    e.stopPropagation();
                    setStatusFilter((prev) => {
                      const arr = Array.isArray(prev) ? prev : [];
                      if (arr.includes(status)) {
                        return arr.filter((s) => s !== status);
                      }
                      return [...arr, status];
                    });
                    setCurrentPage(1);
                  }}
                  style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: meta.accent, flexShrink: 0 }}
                />
                <span className="pj-stat-label">{displayStatus}</span>
              </div>
              <div className="pj-stat-value">{summary[status] || 0}</div>
            </div>
          );
        })}
        <div
          onClick={() => {
            setOverdueOnly((prev) => !prev);
            setStatusFilter([]);
            setCurrentPage(1);
          }}
          className={`pj-stat-card is-alert${overdueOnly ? ' is-active' : ''}`}
          style={{ '--accent': TASK_DEADLINE_META.accent, '--tint': TASK_DEADLINE_META.tint }}
        >
          <div className="pj-stat-top">
            <span className="pj-stat-label">Overdue</span>
          </div>
          <div className="pj-stat-value">{overdueTasksCount}</div>
        </div>
      </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'row-reverse',
            justifyContent: 'space-between',
            gap: '12px',
            alignItems: 'center',
            padding: '14px 16px',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            marginBottom: '16px',
            flexWrap: 'nowrap'
          }}
        >
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'nowrap', flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => setViewMode((prev) => (prev === 'kanban' ? 'list' : 'kanban'))}
              style={{
                ...toolbarButtonStyle,
                background: '#edf2f7',
                color: '#4a5568',
              }}
            >
              {viewMode === 'kanban' ? 'SWITCH TO LIST' : 'SWITCH TO KANBAN'}
            </button>
            {canAddTask && (
            <button
              type="button"
              onClick={openAddTaskModal}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                flexShrink: 0,
                height: 34,
                padding: '0 14px',
                border: 'none',
                background: PRIMARY_GRADIENT,
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                color: '#ffffff',
              }}
            >
              <FiPlus size={15} strokeWidth={2.5} aria-hidden style={{ color: '#fff' }} />
              Add Task
            </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'nowrap', flex: '1 1 auto', minWidth: 0, alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: '1 1 150px', minWidth: 90 }}>
              <FiSearch size={14} aria-hidden style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#98a2b3', pointerEvents: 'none' }} />
              <input
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search Task or Task ID"
                style={{ width: '100%', boxSizing: 'border-box', height: 34, padding: '0 10px 0 32px', border: `1px solid ${TEAMS_TOOLBAR_BORDER}`, borderRadius: 6, fontSize: 12, color: '#344054', background: '#fff' }}
              />
            </div>
            <div style={{ flex: '1 1 130px', minWidth: 0 }}>
              <MultiSelectDropdown
                placeholder="All Projects"
                options={uniqueProjects.filter((project) => project !== 'All')}
                selectedValues={Array.isArray(projectFilter) ? projectFilter : []}
                onChange={setProjectFilter}
                showSearch
                showSelectAll={false}
                showSelectAllCheckbox
              />
            </div>
            {activeTab === 'team' && subordinateIds.length > 0 && (
              <div style={{ flex: '1 1 130px', minWidth: 0 }}>
                <MultiSelectDropdown
                  placeholder="All Team Members"
                  options={subordinateIds}
                  selectedValues={Array.isArray(teamMemberFilter) ? teamMemberFilter : []}
                  onChange={setTeamMemberFilter}
                  getLabel={(id) => {
                    const u = users.find((user) => String(user._id || user.id) === id);
                    return u ? `${u.name} (${u.role || '-'})` : id;
                  }}
                  showSearch={false}
                  showSelectAll={false}
                />
              </div>
            )}
            {activeTab === 'all' && showAllUsersTasksTab && (
              <div style={{ flex: '1 1 130px', minWidth: 0 }}>
                <MultiSelectDropdown
                  placeholder="All Users"
                  options={sortUsersByRole(users).map((user) => String(user._id || user.id))}
                  selectedValues={Array.isArray(teamMemberFilter) ? teamMemberFilter : []}
                  onChange={setTeamMemberFilter}
                  getLabel={(id) => {
                    const u = users.find((user) => String(user._id || user.id) === id);
                    return u ? `${u.name || id} (${u.role || '-'})` : id;
                  }}
                  showSearch
                  showSelectAll={false}
                  showSelectAllCheckbox
                />
              </div>
            )}
            <div style={{ flex: '1 1 110px', minWidth: 0 }}>
              <MultiSelectDropdown
                placeholder="All Priority"
                options={PRIORITIES}
                selectedValues={Array.isArray(priorityFilter) ? priorityFilter : []}
                onChange={setPriorityFilter}
                showSearch={false}
                showSelectAll={false}
              />
            </div>
            <button
              type="button"
              disabled={!hasActiveFilters}
              title={hasActiveFilters ? 'Clear all filters' : 'No filters applied'}
              onClick={() => {
                setSearch('');
                setStatusFilter([]);
                setPriorityFilter([]);
                setProjectFilter([]);
                setMemberFilter(currentUserId || 'All');
                setTeamMemberFilter([]);
                setOverdueOnly(false);
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                flexShrink: 0,
                height: 34,
                padding: '0 14px',
                border: 'none',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                background: hasActiveFilters ? 'rgb(254, 215, 215)' : '#edf2f7',
                color: hasActiveFilters ? 'rgb(197, 48, 48)' : '#a0aec0',
                cursor: hasActiveFilters ? 'pointer' : 'not-allowed',
              }}
            >
              <FiX
                size={14}
                strokeWidth={2.25}
                aria-hidden
                style={{ color: hasActiveFilters ? 'rgb(197, 48, 48)' : '#a0aec0' }}
              />
              Reset Filters
            </button>
          </div>
        </div>

      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          overflow: 'hidden'
        }}
      >
        {viewMode === 'list' ? (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', minWidth: '860px', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '14%' }} />
                </colgroup>
                <thead style={{ background: PRIMARY_GRADIENT }}>
                  <tr>
                    {[
                      ['taskId', 'Task ID'],
                      ['name', 'Task Name'],
                      ['project', 'Project'],
                      ['assignee', 'Assigned To'],
                      ['priority', 'Priority'],
                      ['status', 'Status'],
                      ['startDate', 'Start Date'],
                      ['dueDate', 'Due Date'],
                    ].map(([field, label]) => (
                      <SortableTh
                        key={field}
                        field={field}
                        label={label}
                        sort={sort}
                        onSort={(f) => setSort((prev) => nextSort(prev, f))}
                        style={thStyle}
                      />
                    ))}
                    <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleTasks.map((task) => (
                    <tr key={task.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={tdStyle}>
                        <button
                          type="button"
                          onClick={() => navigate(projectManagementPaths.taskView(task.id))}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            padding: 0,
                            margin: 0,
                            color: '#3182ce',
                            fontWeight: 700,
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontSize: '13px',
                          }}
                          title="Open task details"
                        >
                          {task.taskCode || `T${String(task.id).slice(-4).toUpperCase()}`}
                        </button>
                      </td>
                      <td style={tdStyle}>
                        {task.title || '-'}
                        {Number(task.attachmentCount) > 0 && (
                          <span
                            title={`${task.attachmentCount} attachment${Number(task.attachmentCount) > 1 ? 's' : ''}`}
                            style={{ marginLeft: '6px', cursor: 'default' }}
                          ><MdAttachFile size={15} color="#1a202c" style={{ verticalAlign: '-3px' }} /></span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, maxWidth: '160px' }}>
                        {task.projectId ? (
                          <button
                            type="button"
                            onClick={() => navigate(projectManagementPaths.projectView(task.projectId))}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              padding: 0,
                              margin: 0,
                              color: '#3182ce',
                              fontWeight: 600,
                              cursor: 'pointer',
                              textAlign: 'left',
                              fontSize: '13px',
                              display: 'block',
                              maxWidth: '100%',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            title={task.project}
                          >
                            {task.project}
                          </button>
                        ) : (
                          <span
                            style={{
                              display: 'block',
                              maxWidth: '100%',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            title={task.project}
                          >
                            {task.project}
                          </span>
                        )}
                      </td>
                      <td style={tdStyle}>
                        {(() => {
                          const assigneeNames = getTaskAssigneeNames(task);
                          if (!assigneeNames.length) return '-';
                          const overflow = assigneeNames.length > 3;
                          const shown = overflow ? assigneeNames.slice(0, 2) : assigneeNames;
                          const extra = overflow ? assigneeNames.slice(2) : [];
                          return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              {shown.map((name, idx) => (
                                <span
                                  key={`${task.id}-assignee-${idx}-${name}`}
                                  title={name}
                                  style={assigneeBadgeStyle}
                                >
                                  {getInitials(name)}
                                </span>
                              ))}
                              {extra.length > 0 && (
                                <span title={extra.join(', ')} style={assigneeBadgeStyle}>
                                  +{extra.length}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td style={tdStyle}>
                        <select
                          style={{
                            ...inputControlStyle,
                            width: '100%',
                            minWidth: 'unset',
                            height: '30px',
                            padding: '0 6px',
                            color: task.priority === 'High' ? '#dd6b20' : task.priority === 'Medium' ? '#2b6cb0' : '#2f855a',
                            fontWeight: 600,
                          }}
                          value={task.priority}
                          disabled={!canEditTask(task) || movingTaskId === String(task.id)}
                          onChange={(event) => updateTaskPriorityFromTable(task, event.target.value)}
                        >
                          {PRIORITIES.map((priority) => (
                            <option key={priority} value={priority}>{priority}</option>
                          ))}
                        </select>
                      </td>
                      <td style={tdStyle}>
                        <select
                          style={{ ...inputControlStyle, width: '100%', minWidth: 'unset', height: '30px', padding: '0 6px' }}
                          value={task.status}
                          disabled={!canEditTask(task) || movingTaskId === String(task.id)}
                          onChange={(event) => updateTaskStatusFromTable(task, event.target.value)}
                        >
                          {TASK_STATUSES.map((status) => (
                            <option key={status} value={status}>{STATUS_DISPLAY_LABEL[status] || status}</option>
                          ))}
                        </select>
                      </td>
                      <td style={tdStyle}>{task.startDate}</td>
                      <td style={tdStyle}>{task.dueDate}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '6px' }}>
                          {canEditTask(task) && (
                            <button
                              type="button"
                              style={actionBtnStyle('#e6fffa', '#234e52')}
                              title="Edit"
                              onClick={() => handleEditTask(task.id)}
                            >
                              ✏️
                            </button>
                          )}
                          {canEditTask(task) && getTaskAssigneeNames(task).length > 0 && (
                            <button
                              type="button"
                              style={actionBtnStyle('#eef2ff', '#4c51bf')}
                              title="Send reminder to assignees (in-app + email)"
                              onClick={() => handleSendReminder(task)}
                              disabled={remindingTaskId === task.id}
                            >
                              {remindingTaskId === task.id ? '…' : '🔔'}
                            </button>
                          )}
                          {canDeleteTask() && (
                            <button
                              type="button"
                              style={actionBtnStyle('#fff5f5', '#c53030')}
                              title="Delete"
                              onClick={() => setConfirmDeleteTaskId(task.id)}
                              disabled={deletingTaskId === task.id}
                            >
                              {deletingTaskId === task.id ? '…' : '🗑️'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {visibleTasks.length === 0 && (
                    <tr>
                      <td colSpan={9} style={{ padding: '24px', textAlign: 'center', color: '#718096' }}>
                        {loading
                          ? 'Loading tasks...'
                          : activeTab === 'team' && subordinateIds.length === 0
                            ? 'No team members found under your hierarchy.'
                            : 'No tasks found for selected filters.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {!overdueOnly && (
              <div style={{ padding: '0 16px 14px' }}>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalTasks}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={(value) => {
                    setItemsPerPage(value);
                    setCurrentPage(1);
                  }}
                />
              </div>
            )}
          </>
        ) : (
          <div style={{ padding: '14px 16px 16px', overflowX: 'auto' }}>
            {kanbanLoading ? (
              <div style={{ color: '#718096', padding: '10px 0' }}>Loading board...</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${TASK_STATUSES.length}, minmax(290px, 1fr))`, gap: '12px', minWidth: `${TASK_STATUSES.length * 300}px` }}>
                {TASK_STATUSES.map((status) => {
                  const columnTasks = visibleKanbanTasks.filter((task) => task.status === status);
                  return (
                    <div
                      key={status}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={async () => {
                        if (!draggedTaskId) return;
                        await moveTaskToStatus(draggedTaskId, status);
                        setDraggedTaskId('');
                      }}
                      style={{
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        padding: '8px',
                        minHeight: '420px',
                      }}
                    >
                      <div style={{ padding: '8px', borderRadius: '6px', background: '#edf2f7', fontSize: '13px', fontWeight: 700, color: '#2d3748', marginBottom: '8px' }}>
                        {STATUS_DISPLAY_LABEL[status] || status} ({columnTasks.length})
                      </div>
                      <div style={{ display: 'grid', gap: '8px' }}>
                        {columnTasks.map((task) => (
                          <div
                            key={task.id}
                            draggable
                            onDragStart={() => setDraggedTaskId(String(task.id))}
                            onDragEnd={() => setDraggedTaskId('')}
                            style={{
                              border: '1px solid #dbeafe',
                              background: '#fff',
                              borderRadius: '8px',
                              padding: '10px',
                              cursor: 'grab',
                              opacity: movingTaskId === String(task.id) ? 0.6 : 1,
                            }}
                          >
                            <button
                              type="button"
                              onClick={() => navigate(projectManagementPaths.taskView(task.id))}
                              style={{
                                border: 'none',
                                background: 'transparent',
                                padding: 0,
                                margin: '0 0 4px 0',
                                color: '#2d3748',
                                fontSize: '14px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                textAlign: 'left',
                              }}
                              title="Open task"
                            >
                              {task.title}
                              {Number(task.attachmentCount) > 0 && (
                                <span style={{ marginLeft: '6px' }}><MdAttachFile size={14} color="#1a202c" style={{ verticalAlign: '-3px' }} /></span>
                              )}
                            </button>
                            <div style={{ fontSize: '12px', color: '#718096', marginBottom: '8px' }}>
                              {task.taskCode || `T${String(task.id).slice(-4).toUpperCase()}`} - {task.project}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '11px', color: '#4a5568' }}>{task.assignee || '-'}</span>
                            </div>
                          </div>
                        ))}
                        {columnTasks.length === 0 && (
                          <div style={{ fontSize: '12px', color: '#a0aec0', padding: '8px' }}>
                            Drop tasks here
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {showAddModal && (
        <div
          style={modalOverlayStyle}
        >
          <div style={modalCardStyle} onClick={(event) => event.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, color: '#2d3748' }}>
                {editingTaskId ? 'Edit Task' : 'Add Task'}
              </h3>
              <button
                type="button"
                aria-label="Close"
                onClick={() => {
                  setShowAddModal(false);
                  setEditingTaskId('');
                  setNewTask(initialTaskState);
                  setPendingAttachments([]);
                  setExistingAttachments([]);
                }}
                style={modalCloseBtnStyle}
              >×</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '10px' }}>
              <div>
                <div style={fieldLabelStyle}>Project</div>
                <select
                  value={newTask.projectId}
                  onChange={(event) => setNewTask((prev) => ({ ...prev, projectId: event.target.value }))}
                  style={{ ...inputControlStyle, width: '100%' }}
                >
                  <option value="">No Project</option>
                  {projects.map((project) => (
                    <option key={project._id} value={project._id}>{project.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <div style={fieldLabelStyle}>Task Name</div>
                <input
                  placeholder="Task Name"
                  value={newTask.name}
                  onChange={(event) => setNewTask((prev) => ({ ...prev, name: event.target.value }))}
                  style={{ ...inputControlStyle, width: '100%' }}
                />
              </div>
              {editingTaskId && (
                <div>
                  <div style={fieldLabelStyle}>Status</div>
                  <select
                    value={newTask.status}
                    onChange={(event) => setNewTask((prev) => ({ ...prev, status: event.target.value }))}
                    style={{ ...inputControlStyle, width: '100%' }}
                  >
                    {TASK_STATUSES.map((status) => (
                      <option key={status} value={status}>{STATUS_DISPLAY_LABEL[status] || status}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <div style={fieldLabelStyle}>Priority</div>
                <select
                  value={newTask.priority}
                  onChange={(event) => setNewTask((prev) => ({ ...prev, priority: event.target.value }))}
                  style={{ ...inputControlStyle, width: '100%' }}
                >
                  {PRIORITIES.map((priority) => (
                    <option key={priority} value={priority}>{priority}</option>
                  ))}
                </select>
              </div>
              <div>
                <div style={fieldLabelStyle}>Task Category</div>
                <select
                  value={newTask.taskCategory}
                  onChange={(event) => setNewTask((prev) => ({ ...prev, taskCategory: event.target.value }))}
                  style={{ ...inputControlStyle, width: '100%' }}
                >
                  {TASK_CATEGORIES.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              <div>
                <div style={fieldLabelStyle}>Start Date</div>
                <input
                  type="date"
                  value={newTask.startDate}
                  onChange={(event) => setNewTask((prev) => ({ ...prev, startDate: event.target.value }))}
                  style={{ ...inputControlStyle, width: '100%' }}
                />
              </div>
              <div>
                <div style={fieldLabelStyle}>Due Date</div>
                <input
                  type="date"
                  min={newTask.startDate || undefined}
                  value={newTask.dueDate}
                  onChange={(event) => setNewTask((prev) => ({ ...prev, dueDate: event.target.value }))}
                  style={{ ...inputControlStyle, width: '100%' }}
                />
              </div>
              <UserMultiSelectDropdown
                label="Assignees"
                placeholder="Select assignees"
                users={eligibleProjectUsers}
                selectedIds={newTask.assignees}
                onChange={(ids) => setNewTask((prev) => ({ ...prev, assignees: ids }))}
              />
              <UserMultiSelectDropdown
                label="Followers"
                placeholder="Select followers"
                users={users}
                selectedIds={newTask.followers}
                onChange={(ids) => setNewTask((prev) => ({ ...prev, followers: ids }))}
              />
            </div>
            <div style={{ marginTop: '10px' }}>
              <div style={fieldLabelStyle}>Task Description</div>
              <textarea
                placeholder="Add Description"
                value={newTask.taskDescription}
                onChange={(event) => setNewTask((prev) => ({ ...prev, taskDescription: event.target.value }))}
                style={{ ...inputControlStyle, width: '100%', height: '90px', paddingTop: '10px', resize: 'vertical' }}
              />
            </div>
            {editingTaskId && (
              <div style={{ marginTop: '10px' }}>
                <div style={fieldLabelStyle}>Comment</div>
                <textarea
                  placeholder="Add a comment"
                  value={newTask.comment}
                  onChange={(event) => setNewTask((prev) => ({ ...prev, comment: event.target.value }))}
                  style={{ ...inputControlStyle, width: '100%', height: '70px', paddingTop: '10px', resize: 'vertical' }}
                />
              </div>
            )}
            <div style={{ marginTop: '10px' }}>
              <div style={fieldLabelStyle}>Attachments</div>
              <input
                type="file"
                multiple
                onChange={(event) => {
                  const files = Array.from(event.target.files || []);
                  if (files.length) {
                    setPendingAttachments((prev) => {
                      // Only newly added files count toward the limit; existing
                      // attachments already on the task are ignored.
                      const remainingSlots = MAX_NEW_ATTACHMENTS - prev.length;
                      if (remainingSlots <= 0) {
                        toast.error(`You can upload up to ${MAX_NEW_ATTACHMENTS} new files.`);
                        return prev;
                      }
                      if (files.length > remainingSlots) {
                        toast.error(`You can upload up to ${MAX_NEW_ATTACHMENTS} new files.`);
                      }
                      return [...prev, ...files.slice(0, remainingSlots)];
                    });
                  }
                  event.target.value = '';
                }}
                style={{ ...inputControlStyle, width: '100%', height: 'auto', padding: '8px' }}
              />
              {pendingAttachments.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '6px' }}>
                  {pendingAttachments.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '12px',
                        color: '#4a5568',
                        background: '#f7fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        padding: '4px 8px',
                      }}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {file.name} ({(file.size / 1024).toFixed(1)} KB)
                      </span>
                      <button
                        type="button"
                        aria-label={`Remove ${file.name}`}
                        onClick={() => setPendingAttachments((prev) => prev.filter((_, i) => i !== index))}
                        style={{ border: 'none', background: 'transparent', color: '#e53e3e', cursor: 'pointer', fontWeight: 700 }}
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
              {editingTaskId && existingAttachments.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '6px' }}>
                  {existingAttachments.slice(0, VISIBLE_ATTACHMENT_LIMIT).map((file) => (
                    <div
                      key={file._id || file.url}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '12px',
                        background: '#ebf8ff',
                        border: '1px solid #bee3f8',
                        borderRadius: '6px',
                        padding: '4px 8px',
                      }}
                    >
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          textDecoration: 'none',
                          color: '#2b6cb0',
                          fontWeight: 600,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={file.fileName}
                      >
                        <MdAttachFile size={14} color="#1a202c" style={{ verticalAlign: '-3px', marginRight: '2px' }} />{file.fileName}
                      </a>
                      {file._id && (
                        <button
                          type="button"
                          aria-label={`Remove ${file.fileName}`}
                          title="Remove attachment"
                          onClick={() => handleRemoveExistingAttachment(file._id)}
                          disabled={removingAttachmentId === String(file._id)}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            color: '#e53e3e',
                            cursor: removingAttachmentId === String(file._id) ? 'not-allowed' : 'pointer',
                            opacity: removingAttachmentId === String(file._id) ? 0.6 : 1,
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >{removingAttachmentId === String(file._id) ? '…' : '🗑'}</button>
                      )}
                    </div>
                  ))}
                  {existingAttachments.length > VISIBLE_ATTACHMENT_LIMIT && (
                    <button
                      type="button"
                      onClick={() => navigate(projectManagementPaths.taskView(editingTaskId))}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        padding: 0,
                        margin: '2px 0 0 2px',
                        color: '#2b6cb0',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        textAlign: 'left',
                        alignSelf: 'flex-start',
                      }}
                      title="Open task to see all attachments"
                    >
                      See more ({existingAttachments.length - VISIBLE_ATTACHMENT_LIMIT})
                    </button>
                  )}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '14px' }}>
              <button
                type="button"
                onClick={() => {
                  setShowAddModal(false);
                  setEditingTaskId('');
                  setNewTask(initialTaskState);
                  setPendingAttachments([]);
                  setExistingAttachments([]);
                }}
                style={smallButtonStyle}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitTask}
                disabled={savingTask}
                style={{
                  ...smallButtonStyle,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                }}
              >
                {savingTask ? 'Saving...' : (editingTaskId ? 'Update Task' : 'Add Task')}
              </button>
            </div>
          </div>
        </div>
      )}

      <TaskReminderModal
        isOpen={Boolean(reminderTask)}
        taskName={reminderTask?.title || reminderTask?.name}
        assignees={reminderTask ? getReminderAssignees(reminderTask) : []}
        currentUserId={currentUserId}
        sending={Boolean(remindingTaskId)}
        onClose={() => {
          if (!remindingTaskId) setReminderTask(null);
        }}
        onSubmit={submitReminder}
      />

      {Boolean(confirmDeleteTaskId) && (
        <div
          style={modalOverlayStyle}
          onClick={() => {
            if (!deletingTaskId) setConfirmDeleteTaskId('');
          }}
        >
          <div
            style={{ ...modalCardStyle, width: '420px', maxWidth: '92vw' }}
            onClick={(event) => event.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: '8px', color: '#2d3748' }}>Delete Task</h3>
            <div style={{ color: '#4a5568', fontSize: '14px', lineHeight: 1.5 }}>
              Are you sure you want to delete this task? It will be moved to the Trash, where you can restore it or delete it permanently.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button
                type="button"
                onClick={() => setConfirmDeleteTaskId('')}
                disabled={Boolean(deletingTaskId)}
                style={smallButtonStyle}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDeleteTask(confirmDeleteTaskId)}
                disabled={Boolean(deletingTaskId)}
                style={{
                  ...smallButtonStyle,
                  border: '1px solid #feb2b2',
                  background: '#fff5f5',
                  color: '#c53030',
                }}
              >
                {deletingTaskId ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const thStyle = {
  textAlign: 'left',
  padding: '8px 10px',
  fontSize: '12px',
  fontWeight: 600,
  color: '#ffffff'
};

const tdStyle = {
  padding: '10px 8px',
  color: '#4a5568',
  fontSize: '13px',
  wordBreak: 'break-word'
};

const assigneeBadgeStyle = {
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  background: '#6b5bd2',
  color: '#fff',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '0.2px',
  cursor: 'default',
};

const inputControlStyle = {
  minWidth: '140px',
  height: '34px',
  border: '1px solid #e2e8f0',
  borderRadius: '6px',
  padding: '0 12px',
  fontSize: '12px',
  fontFamily: 'inherit',
  background: '#fff',
  color: '#2d3748'
};


const toolbarButtonStyle = {
  ...inputControlStyle,
  minWidth: 'unset',
  border: '1px solid #e2e8f0',
  background: '#fff',
  color: '#4a5568',
  fontWeight: 600,
  padding: '0 10px',
  lineHeight: 1,
  cursor: 'pointer',
};

const smallButtonStyle = {
  height: '30px',
  border: '1px solid #e2e8f0',
  borderRadius: '6px',
  background: '#fff',
  color: '#4a5568',
  fontSize: '12px',
  fontWeight: 600,
  padding: '0 10px',
  cursor: 'pointer',
};

const actionBtnStyle = (background, color) => ({
  padding: '6px 10px',
  background,
  color,
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '12px',
  fontWeight: 600
});

const modalOverlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1200,
};

const modalCloseBtnStyle = {
  border: 'none',
  background: 'transparent',
  fontSize: '24px',
  lineHeight: 1,
  cursor: 'pointer',
  color: '#718096',
  padding: '2px 6px',
};

const modalCardStyle = {
  width: '680px',
  maxWidth: '90vw',
  maxHeight: '90vh',
  overflowY: 'auto',
  background: '#fff',
  borderRadius: '10px',
  padding: '16px',
  boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
};

const userDropdownPanelStyle = {
  position: 'absolute',
  top: '56px',
  left: 0,
  right: 0,
  zIndex: 30,
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
  padding: '10px',
};

const fieldLabelStyle = {
  fontSize: '12px',
  color: '#4a5568',
  fontWeight: 600,
  marginBottom: '4px',
};

const selectAllBtnStyle = {
  border: '1px solid #cbd5e0',
  background: '#f7fafc',
  color: '#2d3748',
  borderRadius: '6px',
  padding: '4px 10px',
  fontSize: '12px',
  fontWeight: 600,
  cursor: 'pointer',
  marginBottom: '8px',
};

export default TasksListPage;
